import Link from 'next/link';
import { Trans, useTranslation } from 'next-i18next';
import type { CSSProperties } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { FaArrowRight, FaGlobe } from 'react-icons/fa6';

import { BTN_SECONDARY_SM } from '@/constants/button-classes';
import type { FlatMapPop } from '@/constants/flat-map-data';
import { FLAT_MAP_COUNTRY_URLS, FLAT_MAP_DOT_BLUE, FLAT_MAP_DOT_GREEN, FLAT_MAP_FALLBACK_LAND, FLAT_MAP_LAND_URLS } from '@/constants/flat-map-data';

type Polygon = number[][];

interface TopoTransform {
  scale: [number, number];
  translate: [number, number];
}

interface TopoGeometry {
  type: 'Polygon' | 'MultiPolygon' | 'GeometryCollection';
  arcs?: number[][] | number[][][];
  geometries?: TopoGeometry[];
}

interface TopoJson {
  arcs: number[][][];
  transform: TopoTransform;
  objects: Record<string, TopoGeometry>;
}

interface ProjectedPoint {
  x: number;
  y: number;
}

interface PopPoint extends FlatMapPop {
  i: number;
  sx: number;
  sy: number;
  color: string;
  aura: number;
  animated: boolean;
  delay: string;
  duration: string;
}

interface PopCssVars extends CSSProperties {
  '--pop-color': string;
  '--pop-delay': string;
  '--pop-duration': string;
}

interface StarCssVars extends CSSProperties {
  '--star-opacity': number;
  '--star-delay': string;
  '--star-duration': string;
}

const LAT_MIN = -58;
const LAT_MAX = 84;
const CENTER_LNG = 10;
const VIEWPORT_WIDTH = 1600;
const STAR_COUNT = 260;

const toMercatorY = (lat: number) => Math.log(Math.tan(Math.PI / 4 + (lat * Math.PI) / 180 / 2));
const Y_MIN = toMercatorY(LAT_MIN);
const Y_MAX = toMercatorY(LAT_MAX);
const VIEWPORT_HEIGHT = Math.round((VIEWPORT_WIDTH / (2 * Math.PI)) * (Y_MAX - Y_MIN));

const TOPO_CACHE: Partial<Record<'land' | 'countries', Polygon[]>> = {};
const TOPO_PROMISES: Partial<Record<'land' | 'countries', Promise<Polygon[] | null>>> = {};

function mercator(lng: number, lat: number, width: number, height: number, centerLng: number): ProjectedPoint {
  let lambda = ((lng - centerLng) * Math.PI) / 180;
  while (lambda > Math.PI) lambda -= 2 * Math.PI;
  while (lambda < -Math.PI) lambda += 2 * Math.PI;
  const clampedLat = Math.max(LAT_MIN, Math.min(LAT_MAX, lat));
  const x = (lambda / Math.PI) * (width / 2) + width / 2;
  const yRaw = toMercatorY(clampedLat);
  const y = height - ((yRaw - Y_MIN) / (Y_MAX - Y_MIN)) * height;
  // Math.log/Math.tan in toMercatorY can differ by a couple of ULPs between the server's and the
  // browser's transcendental-function implementations, which surfaces as an SVG `cy` hydration
  // mismatch on values derived straight from this function (e.g. popPoints' sx/sy). Rounding here
  // keeps every consumer deterministic without each call site having to remember to do it.
  return { x: Math.round(x * 1000) / 1000, y: Math.round(y * 1000) / 1000 };
}

function flatHash(value: string): number {
  let hash = 0;
  // eslint-disable-next-line no-bitwise
  for (let charIndex = 0; charIndex < value.length; charIndex += 1) hash = (hash * 31 + value.charCodeAt(charIndex)) | 0;
  return Math.abs(hash);
}

function resolveAura(tier: 0 | 1 | 2 | 3): number {
  if (tier <= 1) return 8;
  if (tier === 2) return 6;
  return 4.5;
}

function resolveStarColor(hue: number): string {
  if (hue < 0.55) return '#E8F1FF';
  if (hue < 0.85) return '#9FC5FF';
  return '#5A8FE8';
}

function decodeFlatTopo(topo: TopoJson, objectName: 'land' | 'countries'): Polygon[] {
  const { arcs, transform, objects } = topo;
  const { scale, translate } = transform;
  const decodedArcs: Polygon[] = arcs.map((arc) => {
    let x = 0;
    let y = 0;
    return arc.map(([dx, dy]) => {
      x += dx ?? 0;
      y += dy ?? 0;
      return [x * scale[0] + translate[0], y * scale[1] + translate[1]];
    });
  });
  const resolveArc = (arcIndex: number): Polygon => {
    if (arcIndex >= 0) return decodedArcs[arcIndex] ?? [];
    // eslint-disable-next-line no-bitwise
    const reversed = decodedArcs[~arcIndex];
    return reversed ? reversed.slice().reverse() : [];
  };
  const resolveRing = (indices: number[]): Polygon => {
    const ring: Polygon = [];
    indices.forEach((arcIndex, segmentIndex) => {
      const segment = resolveArc(arcIndex);
      ring.push(...(segmentIndex === 0 ? segment : segment.slice(1)));
    });
    return ring;
  };
  const polygons: Polygon[] = [];
  const walk = (geom: TopoGeometry) => {
    if (geom.type === 'Polygon' && Array.isArray(geom.arcs) && Array.isArray(geom.arcs[0])) {
      polygons.push(resolveRing(geom.arcs[0] as number[]));
    } else if (geom.type === 'MultiPolygon' && Array.isArray(geom.arcs)) {
      (geom.arcs as number[][][]).forEach((polygon) => {
        if (polygon[0]) polygons.push(resolveRing(polygon[0]));
      });
    } else if (geom.type === 'GeometryCollection' && geom.geometries) {
      geom.geometries.forEach(walk);
    }
  };
  const root = objects[objectName];
  if (root) walk(root);
  return polygons;
}

async function loadFlatTopo(kind: 'land' | 'countries'): Promise<Polygon[] | null> {
  const cached = TOPO_CACHE[kind];
  if (cached) return cached;
  const pending = TOPO_PROMISES[kind];
  if (pending) return pending;
  const urls = kind === 'land' ? FLAT_MAP_LAND_URLS : FLAT_MAP_COUNTRY_URLS;
  const promise = (async () => {
    /* eslint-disable no-await-in-loop, no-continue */
    for (const url of urls) {
      try {
        const response = await fetch(url);
        if (!response.ok) continue;
        const json = (await response.json()) as TopoJson;
        const decoded = decodeFlatTopo(json, kind);
        TOPO_CACHE[kind] = decoded;
        return decoded;
      } catch {
        continue;
      }
    }
    /* eslint-enable no-await-in-loop, no-continue */
    return null;
  })();
  TOPO_PROMISES[kind] = promise;
  return promise;
}

const BRAZIL_HUBS = ['São Paulo', 'Rio de Janeiro', 'Brasília', 'Fortaleza'];
const INTL_REGIONS = ['NA', 'EU', 'LATAM', 'APAC'];

const useReveal = () => {
  const ref = useRef<HTMLElement | null>(null);
  const [seen, setSeen] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;
    // Without this guard `new IntersectionObserver` throws inside the effect, and with no error
    // boundary above this section React 18 unmounts the whole root — a blank page instead of a
    // degraded map. Revealing immediately is the right fallback rather than leaving the figure
    // bare: it is exactly how this section behaved before the decoration was gated on scroll.
    if (typeof IntersectionObserver === 'undefined') {
      setSeen(true);
      return undefined;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setSeen(true);
            observer.disconnect();
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -50px 0px' }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);
  return { ref, seen };
};

function buildPaths(data: Polygon[] | null, width: number, height: number, centerLng: number): string[] {
  if (!data) return [];
  const paths: string[] = [];
  data.forEach((poly) => {
    const segments: ProjectedPoint[][] = [];
    let current: ProjectedPoint[] = [];
    let prevX: number | null = null;
    for (let pointIndex = 0; pointIndex < poly.length; pointIndex += 1) {
      const coord = poly[pointIndex];
      // eslint-disable-next-line no-continue
      if (!coord) continue;
      const [lng, lat] = coord as [number, number];
      if (lat < LAT_MIN) {
        if (current.length > 1) segments.push(current);
        current = [];
        prevX = null;
        // eslint-disable-next-line no-continue
        continue;
      }
      const projected = mercator(lng, lat, width, height, centerLng);
      if (prevX !== null && Math.abs(projected.x - prevX) > width / 2) {
        if (current.length > 1) segments.push(current);
        current = [];
      }
      current.push(projected);
      prevX = projected.x;
    }
    if (current.length > 1) segments.push(current);
    segments.forEach((segment) => {
      const points = segment.map((point) => `${point.x.toFixed(1)},${point.y.toFixed(1)}`);
      if (points.length > 2) paths.push(`M${points.join(' L')} Z`);
    });
  });
  return paths;
}

interface FlatStarsProps {
  width: number;
  height: number;
}

function FlatStars({ width, height }: FlatStarsProps) {
  const stars = useMemo(() => {
    /* eslint-disable no-bitwise */
    const hash = (a: number, b: number) => {
      let h = (a * 374761393 + b * 668265263) | 0;
      h = (h ^ (h >>> 13)) >>> 0;
      return (h & 0xffff) / 0xffff;
    };
    /* eslint-enable no-bitwise */
    return Array.from({ length: STAR_COUNT }, (_, starIndex) => ({
      x: hash(starIndex, 1) * width,
      y: hash(starIndex, 2) * height,
      r: 0.5 + hash(starIndex, 3) * 0.8,
      hue: hash(starIndex, 4),
      opacity: 0.24 + hash(starIndex, 5) * 0.34,
      delay: `${-(hash(starIndex, 6) * 5).toFixed(2)}s`,
      duration: `${3.2 + hash(starIndex, 7) * 2.8}s`,
    }));
  }, [width, height]);

  return (
    <g aria-hidden='true'>
      {stars.map((star, starIndex) => {
        const color = resolveStarColor(star.hue);
        const style: StarCssVars = { '--star-opacity': star.opacity, '--star-delay': star.delay, '--star-duration': star.duration };
        return <circle key={starIndex} className='flatmap-star' data-testid='flat-map-star' cx={star.x} cy={star.y} r={star.r} fill={color} style={style} />;
      })}
    </g>
  );
}

interface FlatMapCanvasProps {
  mapTitle: string;
  mapDesc: string;
}

export function FlatMapCanvas({ mapTitle, mapDesc }: FlatMapCanvasProps) {
  const { ref, seen } = useReveal();

  const [landData, setLandData] = useState<Polygon[]>(FLAT_MAP_FALLBACK_LAND);
  const [borderData, setBorderData] = useState<Polygon[] | null>(null);
  const [hoveredPop, setHoveredPop] = useState<PopPoint | null>(null);
  const [pops, setPops] = useState<FlatMapPop[]>([]);

  // The POP table is 23 KB of coordinates that only the revealed figure ever renders, so it rides in its
  // own module and is import()ed here rather than sitting in the graph of whatever page mounts this
  // figure. It is a separate effect from the topology fetch below on purpose: bundling it into that
  // Promise.all would make the markers wait on a CDN round trip they do not depend on.
  useEffect(() => {
    if (!seen) return undefined;
    let cancelled = false;
    import('@/constants/flat-map-pops')
      .then((popsModule) => {
        if (!cancelled) setPops(popsModule.FLAT_MAP_POPS);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [seen]);

  useEffect(() => {
    if (!seen) return undefined;
    let cancelled = false;
    const load = async () => {
      const [land, countries] = await Promise.all([loadFlatTopo('land'), loadFlatTopo('countries')]);
      if (cancelled) return;
      if (land) setLandData(land);
      if (countries) setBorderData(countries);
    };
    load().catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [seen]);

  const landPathD = useMemo(() => buildPaths(landData, VIEWPORT_WIDTH, VIEWPORT_HEIGHT, CENTER_LNG).join(' '), [landData]);
  const borderPathD = useMemo(() => buildPaths(borderData, VIEWPORT_WIDTH, VIEWPORT_HEIGHT, CENTER_LNG).join(' '), [borderData]);

  const popPoints: PopPoint[] = useMemo(
    () =>
      pops
        .filter((pop) => pop.lat > LAT_MIN)
        .map((pop, popIndex) => {
          const projected = mercator(pop.lng, pop.lat, VIEWPORT_WIDTH, VIEWPORT_HEIGHT, CENTER_LNG);
          const seed = flatHash(pop.city);
          const color = seed % 5 === 0 ? FLAT_MAP_DOT_BLUE : FLAT_MAP_DOT_GREEN;
          return {
            ...pop,
            i: popIndex,
            sx: projected.x,
            sy: projected.y,
            color,
            aura: resolveAura(pop.tier),
            animated: pop.tier <= 2,
            delay: `${-(seed % 4200) / 1000}s`,
            duration: `${2.7 + (seed % 1400) / 1000}s`,
          };
        }),
    [pops]
  );

  return (
    <figure ref={ref} className={`flatmap-wrap relative m-0 block w-full overflow-hidden rounded-[12px] border border-[var(--np-line)] bg-[var(--np-bg-1)] ${seen ? 'is-animated' : ''}`}>
      <div data-testid='flat-map-tooltip-region' aria-live='polite' aria-atomic='true' className='sr-only'>
        {hoveredPop ? `${hoveredPop.city}, ${hoveredPop.country}` : ''}
      </div>
      {hoveredPop && (
        <div aria-hidden='true' data-testid='flat-map-tooltip' className='flatmap-tooltip pointer-events-none absolute z-[3] ms-2 mt-2 whitespace-nowrap rounded-[5px] border border-[rgba(156,255,46,0.35)] bg-[rgba(10,12,14,0.92)] px-2 py-1 leading-none backdrop-blur-md' style={{ left: `${(hoveredPop.sx / VIEWPORT_WIDTH) * 100}%`, top: `${(hoveredPop.sy / VIEWPORT_HEIGHT) * 100}%` }}>
          <div className='text-[11px] font-semibold tracking-[0.01em] text-white'>{hoveredPop.city}</div>
          {hoveredPop.country && <div className='mt-0.5 text-[9px] font-medium uppercase tracking-[0.04em] text-[rgba(170,255,70,0.85)]'>{hoveredPop.country}</div>}
        </div>
      )}
      <svg viewBox={`0 0 ${VIEWPORT_WIDTH} ${VIEWPORT_HEIGHT}`} preserveAspectRatio='xMidYMid meet' className='block size-full' role='img' aria-labelledby='flat-map-title flat-map-desc'>
        <title id='flat-map-title'>{mapTitle}</title>
        <desc id='flat-map-desc'>{mapDesc}</desc>
        <defs>
          <radialGradient id='flatMapAtmos' cx='50%' cy='50%' r='62%'>
            <stop offset='70%' stopColor='transparent' />
            <stop offset='100%' stopColor='rgba(156,255,46,0.14)' />
          </radialGradient>
        </defs>

        {/* Decorative and aria-hidden, so nothing here is content: 260 stars plus ~900 circles for
            the POP markers were 238 KB of the home page's server-rendered HTML and several hundred
            animated compositor layers, all of it parsed before the first paint. Gating on `seen`
            defers them to the moment the figure scrolls into view. The <svg> keeps a fixed viewBox
            and the land paths stay server-rendered, so the figure's box never changes size — no CLS.
            The POP markers carry hover handlers, which is fine: they cannot be hovered off-screen. */}
        {seen && <FlatStars width={VIEWPORT_WIDTH} height={VIEWPORT_HEIGHT} />}

        <g className='pointer-events-none'>
          {landPathD && <path fill='rgba(0,0,0,0.85)' stroke='none' d={landPathD} transform='translate(5.2, 5.2)' />}
          {landPathD && <path fill='rgba(0,0,0,0.9)' stroke='none' d={landPathD} transform='translate(3.9, 3.9)' />}
          {landPathD && <path fill='rgba(0,0,0,0.78)' stroke='none' d={landPathD} transform='translate(2.5, 2.5)' />}
          {landPathD && <path fill='rgba(0,0,0,0.6)' stroke='none' d={landPathD} transform='translate(1.2, 1.2)' />}
          {landPathD && <path fill='#14181c' stroke='rgba(170,255,70,0.5)' strokeWidth='0.5' strokeLinejoin='round' d={landPathD} />}
          {borderPathD && <path fill='none' stroke='rgba(170,255,70,0.5)' strokeWidth='0.5' strokeLinejoin='round' d={borderPathD} />}
          {landPathD && <path fill='none' stroke='rgba(210,255,150,0.36)' strokeWidth='0.7' strokeLinejoin='round' d={landPathD} transform='translate(-1.0, -1.0)' />}
        </g>

        <g className='isolate' aria-hidden='true'>
          {seen &&
            popPoints.map((pop) => {
              const style: PopCssVars = { '--pop-color': pop.color, '--pop-delay': pop.delay, '--pop-duration': pop.duration, cursor: 'pointer' };
              return (
                <g
                  key={pop.i}
                  data-testid='flat-map-pop'
                  className={`flatmap-pop ${pop.animated ? 'is-animated-pop' : ''}`}
                  onMouseEnter={(event) => {
                    event.stopPropagation();
                    setHoveredPop(pop);
                  }}
                  onMouseLeave={() => setHoveredPop(null)}
                  style={style}
                >
                  <circle className='flatmap-pop-aura' fill='currentColor' opacity='0.2' cx={pop.sx} cy={pop.sy} r={pop.aura} style={{ transformOrigin: `${pop.sx}px ${pop.sy}px` }} />
                  <circle fill='currentColor' opacity='0.96' cx={pop.sx} cy={pop.sy} r={2} />
                  <circle fill='transparent' pointerEvents='all' cx={pop.sx} cy={pop.sy} r={10} />
                </g>
              );
            })}
        </g>
      </svg>
      <figcaption className='sr-only'>{mapDesc}</figcaption>
    </figure>
  );
}

export default function FlatMapSection() {
  const { t } = useTranslation('components/flat-map');

  return (
    <section id='cobertura' aria-labelledby='flat-map-heading' className='relative'>
      <div className='mx-auto flex w-full max-w-[1400px] flex-col gap-10 p-5 xl:pb-20 xl:pt-10'>
        <div className='mx-auto flex max-w-[760px] flex-col items-center gap-5 text-center'>
          <p className='inline-flex items-center gap-2 font-mono text-[11px] font-bold uppercase tracking-[0.22em] text-[var(--np-green)] max-[640px]:text-[10px] max-[640px]:tracking-[0.18em]'>
            <FaGlobe size={14} aria-hidden='true' />
            {t('flat_map.eyebrow')}
          </p>
          <h2 id='flat-map-heading' className='text-[56px] font-bold leading-none tracking-[-0.03em] text-white max-[1199px]:text-[clamp(40px,5.5vw,56px)] max-[960px]:!text-[clamp(34px,5.2vw,52px)] max-[960px]:leading-[1.05] max-[640px]:!text-[clamp(28px,8vw,38px)] max-[380px]:!text-[clamp(26px,9vw,34px)] [&_em]:not-italic [&_em]:text-[var(--np-green)]'>
            <Trans i18nKey='flat_map.title' t={t} components={{ em: <em /> }} />
          </h2>
          <p className='text-base leading-[1.65] text-[var(--np-fg-dim)]'>{t('flat_map.subtitle', { servers: t('flat_map.servers'), countries: t('flat_map.countries') })}</p>
        </div>

        <FlatMapCanvas mapTitle={t('flat_map.map_title')} mapDesc={t('flat_map.map_desc')} />

        <nav aria-label={t('flat_map.coverage_label')} className='sr-only'>
          <h3>{t('flat_map.coverage_label')}</h3>
          <ul>
            {BRAZIL_HUBS.map((hub) => (
              <li key={hub}>{hub}</li>
            ))}
            {INTL_REGIONS.map((region) => (
              <li key={region}>{region}</li>
            ))}
          </ul>
        </nav>

        <div className='flex justify-center'>
          <Link href='/servers' className={BTN_SECONDARY_SM}>
            {t('flat_map.cta')}
            <FaArrowRight size={14} aria-hidden='true' className='rtl:-scale-x-100' />
          </Link>
        </div>
      </div>
    </section>
  );
}
